import { useState } from "react";
import businessIllustration from "@/assets/froppio-business.svg";
import { BusinessFields } from "@/features/business/components/BusinessFields";
import { useCreateBusiness, useUpdateBusiness } from "@/features/business/hooks/businessQueries";
import type { Business } from "@/features/business/types/businessTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormPage } from "@/shared/components/ui/FormPage";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";
import { defaultBusinessTimezone } from "@/shared/utils/timezones";

interface BusinessOnboardingPageProps {
  business?: Business;
  onSaved?: (business: Business) => void;
}

export function BusinessOnboardingPage({ business, onSaved }: BusinessOnboardingPageProps) {
  const createBusiness = useCreateBusiness();
  const updateBusiness = useUpdateBusiness(business?.id ?? "");
  const mutation = business ? updateBusiness : createBusiness;
  const [formData, setFormData] = useState({
    name: business?.name ?? "",
    industry: business?.industry ?? "",
    timezone: business?.timezone ?? defaultBusinessTimezone(),
    currency: business?.currency ?? "USD",
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mutation.isPending) return;
    const details = { ...formData, name: formData.name.trim() };
    if (business) {
      updateBusiness.mutate(details, { onSuccess: onSaved });
    } else {
      createBusiness.mutate({ ...details, settings: {} }, { onSuccess: onSaved });
    }
  };

  return (
    <div className="h-dvh">
      <FormPage
        header={<SetupHeader title="Your business, your way" illustration={businessIllustration} step={2} stage="Business" />}
        onSubmit={handleSubmit}
        narrow
        actions={
          <Button type="submit" size="touch" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : business ? "Save and review" : "Continue"}
          </Button>
        }
      >
        <FieldGroup disabled={mutation.isPending}>
          <BusinessFields value={formData} onChange={setFormData} />
        </FieldGroup>
        {mutation.error && <ErrorState message={mutation.error.message || "We couldn't save your business. Please try again."} />}
      </FormPage>
    </div>
  );
}
